import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { WorkOrderDto } from 'src/api';
import { MtxDrawer, MtxDrawerModule } from '@ng-matero/extensions/drawer';
import { WorkorderAssignSpecialistsComponent } from '../workorder-assign-specialists/workorder-assign-specialists.component';
import { WorkorderNotMineComponent } from '../workorder-not-mine/workorder-not-mine.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, defer, from, map } from 'rxjs';
import { WorkorderEmployeeVisitsComponent } from '../workorder-employee-visits/workorder-employee-visits.component';
import { WorkorderRejectValidateComponent } from '../workorder-reject-validate/workorder-reject-validate.component';
import { WorkorderExtraTimeComponent } from '../workorder-extra-time/workorder-extra-time.component';
import { WorkorderOptimizeComponent } from '../workorder-optimize/workorder-optimize.component';
import { TranslateModule } from '@ngx-translate/core';
import { EmployeesRepository } from 'src/employees/employees.repository';

@Component({
  selector: 'app-workorder-card-actions',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MtxDrawerModule, MatTooltipModule, TranslateModule],
  templateUrl: './workorder-card-actions.component.html',
  styleUrls: ['./workorder-card-actions.component.scss'],
})
export class WorkorderCardActionsComponent implements OnInit {
  @Input({ required: true }) workOrder$!: Observable<WorkOrderDto | undefined>;

  assignVisible$!: Observable<boolean>;
  notMineVisible$!: Observable<boolean>;
  employeeVisitsVisible$!: Observable<boolean>;
  optimizeVisible$!: Observable<boolean>;
  workOrderIsFinished$!: Observable<boolean>;
  workOrderIsValidated$!: Observable<boolean>;
  isSupervisor$!: Observable<boolean>;

  constructor(
    private drawer: MtxDrawer, 
    private employeesRepository: EmployeesRepository
  ) {}

  ngOnInit(): void {
    this.assignVisible$ = this.workOrder$.pipe(
      map((workOrder) => {
        return (
          workOrder?.workOrderState === 'Unassigned' ||
          workOrder?.workOrderState === 'Assigned' ||
          workOrder?.workOrderState === 'Working'
        );
      })
    );

    this.notMineVisible$ = this.workOrder$.pipe(
      map((workOrder) => {
        return workOrder?.workOrderState == 'Unassigned';
      })
    );

    this.employeeVisitsVisible$ = this.workOrder$.pipe(
      map((workOrder) => {
        return workOrder?.workOrderState == 'Assigned' || workOrder?.workOrderState == 'Working';
      })
    );

    this.workOrderIsFinished$ = this.workOrder$.pipe(
      map((workOrder) => {
        return workOrder?.workOrderState == 'Finished';
      })
    );

    this.optimizeVisible$ = this.workOrder$.pipe(
      map((workOrder) => {
        return (
          !workOrder?.isBillable &&
          (workOrder?.workOrderState === 'Unassigned' || workOrder?.workOrderState === 'Assigned')
        );
      })
    );

    this.workOrderIsValidated$ = this.workOrder$.pipe(
      map((workOrder) => {
        return workOrder?.workOrderState === 'Validated';
      })
    );

    this.isSupervisor$ = this.employeesRepository.getLoggedInEmployeeRole().pipe(
      map((role) => {
        return role === 'Supervisor'
      })
    ); 
  }

  openAssign(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderAssignSpecialistsComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  openNotMine(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderNotMineComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  openEmployeeVisits(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderEmployeeVisitsComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  openValidateWorkOrder(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderRejectValidateComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder: workOrder, validateWorkOrder: true },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  openRejectWorkOrder(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderRejectValidateComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder: workOrder, validateWorkOrder: false },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  openWorkOrderExtraTime(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderExtraTimeComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder: workOrder },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  openWorkOrderOptimize(workOrder: WorkOrderDto) {
    const drawerRef = this.drawer.open(WorkorderOptimizeComponent, {
      position: 'right',
      width: this.getDrawerInnerWidth(),
      hasBackdrop: true,
      disableClose: false,
      closeOnNavigation: true,
      data: { workOrder: workOrder },
    });

    drawerRef.afterDismissed().subscribe((result) => {
      console.log('The drawer was dismissed');
    });
  }

  private getDrawerInnerWidth(): string {
    return window.innerWidth > 1000 ? '33vw' : '90vw';
  }
}
